/*
 * File: ErrorComponent.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 19, 2022 at 12:17 AM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 4, 2025 at 5:31 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';

// Components
import { Meta } from '../Meta';
import { Typography } from '../Typography';
import { Button } from '../Button';
import { LocaleLink } from '../LocaleLink';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../constants/base.constants';

// Styles
import * as S from './ErrorComponent.styles';


/**
 * Component
 */

export const ErrorComponent = ({
	title, description, message, buttons, showMeta
}) => {

	// Get partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Define meta content
	const meta = useMemo(() => ({
		title: t('pageError.metaTitle', { partnerName: partner.name }),
		description: t('pageError.metaDescription', { partnerName: partner.name }),
		bodyClasses: 'hide-captcha',
		showSocial: false
	}), [partner, t]);

	// Render component
	return (
		<>
			{/* Meta */}
			{showMeta && (
				<Meta meta={meta} />
			)}

			{/* Component Content */}
			<S.Wrapper>
				<S.ContentContainer>
					<S.MessageContainer>
						<Typography tag="h1" weight="black">
							{title || (
								<Trans t={t} i18nKey="pageError.title">
									Uh oh, we can&apos;t find the page you&apos;re looking for.
								</Trans>
							)}
						</Typography>
						<S.DescriptionContainer>
							<Typography tag="h4" weight="regular">
								{description || (
									<Trans t={t} i18nKey="pageError.description">
										Try going back to the previous page or contact us at
										{' '}
										<LocaleLink to="mailto:support@ourwave.org">support@ourwave.org</LocaleLink>
										{' '}
										for more information.
									</Trans>
								)}
							</Typography>
						</S.DescriptionContainer>
						{message && (
							<Typography tag="h4" weight="bold">
								{message}
							</Typography>
						)}
					</S.MessageContainer>
					<S.ActionContainer $fullWidth={!buttons}>
						{
							buttons ? buttons.map((button) => (
								<S.ButtonLink to={button.link} key={button.text}>
									<Button size={button.size} variant={button.variant || 'solid'}>{button.text}</Button>
								</S.ButtonLink>
							)) : (
								<>
									<S.ButtonLink to="/">
										<Button size="large">
											<Trans t={t} i18nKey="actions.backToHome">
												Back to Home
											</Trans>
										</Button>
									</S.ButtonLink>
									<S.ButtonLink to="/resources">
										<Button size="large" variant="outline">
											<Trans t={t} i18nKey="actions.findResources">
												Find Resources
											</Trans>
										</Button>
									</S.ButtonLink>
								</>
							)
						}
					</S.ActionContainer>
				</S.ContentContainer>
			</S.Wrapper>
		</>
	);
};


/**
 * Configuration
 */

ErrorComponent.displayName = 'ErrorComponent';
ErrorComponent.propTypes = {
	title: PropTypes.string,
	description: PropTypes.node,
	message: PropTypes.string,
	buttons: PropTypes.arrayOf(PropTypes.shape()),
	showMeta: PropTypes.bool
};
ErrorComponent.defaultProps = {
	title: null,
	description: null,
	message: null,
	buttons: null,
	showMeta: false
};
